<template>
  <div>
    <!-- Botón flotante -->
    <button class="chatbot-button" @click="toggleChat" aria-label="Abrir chat">💬</button>

    <!-- Ventana de chat -->
    <transition name="fade">
      <div v-if="open" :class="['chatbot-window', { maximized: isMaximized }]" role="dialog" aria-label="Chat Flowise">
        <div class="chatbot-header">
          <div class="header-left">
            <button class="icon-btn" @click="toggleMax" :title="isMaximized ? t('restore') : t('maximize')">
              {{ isMaximized ? '🗗' : '🗖' }}
            </button>
            <img :src="botAvatar" class="avatar" alt="" />
            <span>Flowise Bot</span>
          </div>

          <div class="header-actions">
            <!-- Selector ES/EN -->
            <div class="segmented">
              <button :class="['seg-btn', { active: language === 'es' }]" @click="setLang('es')">ES</button>
              <button :class="['seg-btn', { active: language === 'en' }]" @click="setLang('en')">EN</button>
            </div>
            <button class="icon-btn warn" @click="clearChat" :title="t('clear')">🗑</button>
            <button class="icon-btn" @click="toggleChat" :title="t('close')">✖</button>
          </div>
        </div>

        <!-- Mensajes -->
        <div class="chatbot-messages" ref="messagesBox">
          <div v-for="(msg, i) in messages" :key="i" :class="['message', msg.role]">

            <!-- Bot con botones -->
            <div v-if="msg.role === 'bot' && msg.type === 'buttons'" class="bubble bot">
              <p class="bubble-text">{{ msg.text }}</p>
              <div class="buttons">
                <button v-for="(opt, idx) in msg.options" :key="idx" @click="selectOption(opt)">
                  {{ opt.label }}
                </button>
              </div>
            </div>

            <!-- Bot normal -->
            <div v-else-if="msg.role === 'bot'" class="bubble bot">
              <span class="bubble-text">{{ msg.text }}</span>
            </div>

            <!-- Usuario -->
            <div v-else-if="msg.role === 'user'" class="bubble user">
              <span class="bubble-text">{{ msg.text }}</span>
            </div>

            <!-- Escribiendo -->
            <div v-else-if="msg.role === 'typing'" class="bubble bot typing">
              <span class="dot"></span><span class="dot"></span><span class="dot"></span>
            </div>
          </div>
        </div>

        <!-- Adjuntos seleccionados -->
        <div v-if="attachedFiles.length" class="attachments">
          <div v-for="(f, idx) in attachedFiles" :key="idx" class="chip">
            <img v-if="f.type.startsWith('image/')" :src="f.preview" alt="" />
            <span class="name">{{ f.name }}</span>
            <button class="x" @click="removeFile(idx)" aria-label="Quitar">×</button>
          </div>
        </div>

        <!-- Entrada + Adjuntar -->
        <div class="chatbot-input">
          <input v-model="question" :placeholder="t('placeholder')" @keyup.enter="ask" :disabled="isLoading"
            aria-label="Entrada de texto" />
          <input ref="fileInput" type="file" multiple @change="onFilesSelected" hidden />
          <button class="attach-btn" :disabled="isLoading" @click="$refs.fileInput.click()"
            :title="t('attach')">📎</button>
          <button @click="ask" class="send-btn" :disabled="isLoading" aria-label="Enviar">➤</button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import flowisePlugin from '~/plugins/flowise'

export default {

  name: 'Chatbot',

  data() {
    const savedLang = localStorage.getItem('epc_chat_lang') || 'es'
    const savedChatId =
      localStorage.getItem('epc_chat_id') ||
      `chat_${Date.now()}_${Math.random().toString(36).slice(2)}`

    localStorage.setItem('epc_chat_id', savedChatId)

    return {
      open: false,
      isMaximized: false,
      isLoading: false,
      question: '',
      messages: [],
      attachedFiles: [],
      language: savedLang,
      chatId: savedChatId,
      botAvatar: 'https://img.icons8.com/?size=100&id=NpWB6GLVTcND&format=png&color=000000',
      selectedProjectId: null,
      selectIA: null, // 0 proyectos / 1 IA
      CHATFLOW_ID: 'eefa18d0-af95-4a5c-8656-52b164e296ac',
    }
  },


  methods: {
    /* 👉 Procesar respuestas del bot */
    handleBotResponse(result) {
      const payload = flowisePlugin.parseBotResponse(result)

      if (payload.type === 'buttons') {
        this.messages.push({ role: 'bot', ...payload, text: this.t('selectProject') })
      } else {
        this.messages.push({ role: 'bot', text: payload.text || JSON.stringify(payload) })
      }
    },

    async selectOption(opt) {
      if (opt?.meta === 'selectIA') {
        await this.chooseIA(opt.value, opt.label)
        return
      }
      this.selectedProjectId = Number(opt.value)
      this.messages.push({ role: 'user', text: opt.label })
      await this.askWithProjectId()
    },

    async chooseIA(value, label) {
      this.selectIA = Number(value)
      this.messages.push({ role: 'user', text: label })
      await this.askWithIASelection()
    },

    async askWithIASelection() {
      if (this.isLoading) return
      this.isLoading = true
      this.messages = flowisePlugin.addTypingMessage(this.messages)
      this.scrollToBottom()

      try {
        const payload = {
          chatflowId: this.CHATFLOW_ID,
          question: '¡Hola muy buenas!',
          chatId: this.chatId,
          overrideConfig: {
            systemMessage: this.t('typingSystem'),
            vars: {
              user_id: this.$store.getters['auth/user']?.id || 'anon',
              token: this.$store.getters['auth/token'] || '',
              project_id: Number(this.selectedProjectId) || 0,
              language: this.language,
              select_ia: Number(this.selectIA),
            },
          },
        }

        const response = await flowisePlugin.sendPrediction(payload, this.$store.getters['auth/token'])

        this.messages = flowisePlugin.removeTypingMessages(this.messages)
        this.handleBotResponse(response.data)
      } catch (e) {
        console.error(e)
        this.messages = flowisePlugin.removeTypingMessages(this.messages)
        this.messages.push({ role: 'bot', text: this.t('error') })
      } finally {
        this.isLoading = false
        this.scrollToBottom()
      }
    },

    async askWithProjectId() {
      if (this.isLoading) return
      this.isLoading = true
      this.messages = flowisePlugin.addTypingMessage(this.messages)
      this.scrollToBottom()

      try {
        const payload = {
          chatflowId: this.CHATFLOW_ID,
          question: 'Hola! Quiero saber las actividades críticas de mi proyecto',
          chatId: this.chatId,
          overrideConfig: {
            systemMessage: this.t('typingSystem'),
            vars: {
              user_id: this.$store.getters['auth/user']?.id || 'anon',
              token: this.$store.getters['auth/token'] || '',
              project_id: this.selectedProjectId,
              language: this.language,
              select_ia: this.selectIA !== null ? Number(this.selectIA) : null,
            },
          },
        }

        const response = await flowisePlugin.sendPrediction(payload, this.$store.getters['auth/token'])

        this.messages = flowisePlugin.removeTypingMessages(this.messages)
        this.handleBotResponse(response.data)
      } catch (e) {
        console.error(e)
        this.messages = flowisePlugin.removeTypingMessages(this.messages)
        this.messages.push({ role: 'bot', text: this.t('error') })
      } finally {
        this.isLoading = false
        this.scrollToBottom()
      }
    },

    /* ======== ask normal ======== */
    async ask() {
      const q = this.question.trim()
      if (!q && !this.attachedFiles.length) return
      if (this.isLoading) return

      this.question = ''
      this.isLoading = true

      if (q) this.messages.push({ role: 'user', text: q })
      this.messages = flowisePlugin.addTypingMessage(this.messages)
      this.scrollToBottom()

      try {
        let uploads = []
        if (this.attachedFiles.length) {
          uploads = await flowisePlugin.uploadAttachments(this.attachedFiles, this.chatId, this.CHATFLOW_ID, this.$store.getters['auth/token'])
        }

        const payload = {
          chatflowId: this.CHATFLOW_ID,
          question: q || this.t('analyzeFiles'),
          chatId: this.chatId,
          uploads: uploads.map(a => ({
            data: a.dataUrl,
            type: a.uploadType,
            name: a.name,
            mime: a.mime,
          })),
          overrideConfig: {
            systemMessage: this.t('typingSystem'),
            vars: {
              user_id: this.$store.getters['auth/user']?.id || 'anon',
              token: this.$store.getters['auth/token'] || '',
              project_id: Number(this.selectedProjectId) || 0,
              language: this.language,
              select_ia: this.selectIA !== null ? Number(this.selectIA) : null,
            },
          },
        }

        const response = await flowisePlugin.sendPrediction(payload, this.$store.getters['auth/token'])

        this.messages = flowisePlugin.removeTypingMessages(this.messages)
        this.handleBotResponse(response.data)
      } catch (e) {
        console.error(e)
        this.messages = flowisePlugin.removeTypingMessages(this.messages)
        this.messages.push({ role: 'bot', text: this.t('error') })
      } finally {
        this.attachedFiles.forEach(it => it.preview && URL.revokeObjectURL(it.preview))
        this.attachedFiles = []
        this.isLoading = false
        this.scrollToBottom()
      }
    },

    /* ====== resto de métodos ====== */
    t(key) {
      return flowisePlugin.translate(this.language, key, this.getDictionaries())
    },

    getDictionaries() {
      return {
        es: {
          maximize: 'Maximizar',
          restore: 'Restaurar',
          clear: 'Limpiar chat',
          close: 'Cerrar',
          confirmClear: '¿Seguro que quieres limpiar el chat? Se borrará la conversación actual.',
          placeholder: 'Escribe tu pregunta…',
          typingSystem: 'Eres un asistente de EPC Tracker. Responde siempre en español (ES).',
          attach: 'Adjuntar archivos',
          selectProject: '👉 Selecciona un proyecto:',
          analyzeFiles: 'Analiza los archivos adjuntos.',
          error: '⚠️ Error al conectar con Flowise',
          helloIntro: '👋 Hola. ¿Qué quieres hacer?',
          talkToAI: '🧠 Hablar con la IA',
          consultProjects: '📁 Consultar proyectos',
          langChanged: 'Idioma cambiado a Español.',
        },
        en: {
          maximize: 'Maximize',
          restore: 'Restore',
          clear: 'Clear chat',
          close: 'Close',
          confirmClear: 'Are you sure you want to clear the chat? Current conversation will be erased.',
          placeholder: 'Type your question…',
          typingSystem: 'You are an EPC Tracker assistant. Always answer in English (EN).',
          attach: 'Attach files',
          selectProject: '👉 Select a project:',
          analyzeFiles: 'Analyze the attached files.',
          error: '⚠️ Error connecting to Flowise',
          helloIntro: '👋 Hi. What would you like to do?',
          talkToAI: '🧠 Talk to the AI',
          consultProjects: '📁 Browse projects',
          langChanged: 'Language switched to English.',
        },
      }
    },

    setLang(code) {
      const result = flowisePlugin.setLanguage({
        currentLang: this.language,
        newLang: code,
        messages: this.messages,
        storageKey: 'epc_chat_lang',
        dictionaries: this.getDictionaries(),
      })

      if (result) {
        this.language = result.language
        this.messages = result.messages
        this.$nextTick(this.scrollToBottom)
      }
    },

    showInitialButtons() {
      this.messages.push({
        role: 'bot',
        type: 'buttons',
        text: this.t('helloIntro'),
        options: [
          { label: this.t('talkToAI'), value: 1, meta: 'selectIA' },
          { label: this.t('consultProjects'), value: 0, meta: 'selectIA' },
        ],
      })
      this.$nextTick(this.scrollToBottom)
    },

    toggleChat() {
      this.open = !this.open
      if (this.open && this.messages.length === 0) {
        this.showInitialButtons()
      }
      this.$nextTick(this.scrollToBottom)
    },

    toggleMax() {
      this.isMaximized = !this.isMaximized
      this.$nextTick(this.scrollToBottom)
    },

    clearChat() {
      const result = flowisePlugin.clearChat({
        messages: this.messages,
        attachedFiles: this.attachedFiles,
        storageKey: 'epc_chat_id',
        dictionaries: this.getDictionaries(),
        language: this.language,
      })

      if (result) {
        this.messages = result.messages
        this.attachedFiles = result.attachedFiles
        this.chatId = result.chatId
        this.selectedProjectId = result.selectedProjectId
        this.selectIA = result.selectIA
        this.showInitialButtons()
        this.$nextTick(this.scrollToBottom)
      }
    },

    onFilesSelected(e) {
      const files = Array.from(e.target.files || [])
      files.forEach(f => {
        const item = { file: f, name: f.name, type: f.type }
        if (f.type.startsWith('image/')) item.preview = URL.createObjectURL(f)
        this.attachedFiles.push(item)
      })
      e.target.value = ''
    },

    removeFile(idx) {
      const item = this.attachedFiles[idx]
      if (item?.preview) URL.revokeObjectURL(item.preview)
      this.attachedFiles.splice(idx, 1)
    },

    scrollToBottom() {
      flowisePlugin.scrollToBottom(this.$refs.messagesBox)
    },
  },
}
</script>


<style scoped>
/* Animación */
.fade-enter-active,
.fade-leave-active {
  transition: opacity .2s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* Botón flotante */
.chatbot-button {
  position: fixed;
  right: 20px;
  bottom: 20px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  color: #fff;
  font-size: 24px;
  background: linear-gradient(135deg, #3B81F6, #6C63FF);
  box-shadow: 0 8px 20px rgba(0, 0, 0, .35);
  transition: transform .15s, box-shadow .15s;
  z-index: 2147483647;
}

.chatbot-button:hover {
  transform: scale(1.07);
  box-shadow: 0 10px 24px rgba(0, 0, 0, .4);
}

/* Ventana */
.chatbot-window {
  position: fixed;
  right: 20px;
  bottom: 84px;
  width: 380px;
  height: min(520px, calc(100vh - 120px));
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 28px rgba(0, 0, 0, .28);
  z-index: 2147483647;
  box-sizing: border-box;
}

.chatbot-window.maximized {
  top: 5vh;
  bottom: auto;
  right: auto;
  left: 50%;
  transform: translateX(-50%);
  width: min(900px, 92vw);
  height: 90vh;
  border-radius: 18px;
}

/* Cabecera */
.chatbot-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, #3B81F6, #6C63FF);
  color: #fff;
  padding: 12px 16px;
  font-weight: 600;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.avatar {
  width: 26px;
  height: 26px;
  border-radius: 50%;
}

/* Botones cabecera */
.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  background: rgba(255, 255, 255, .12);
  color: #fff;
  transition: background .15s, transform .05s;
  font-size: 18px;
  line-height: 1;
}

.icon-btn:hover {
  background: rgba(255, 255, 255, .22);
}

.icon-btn:active {
  transform: scale(.97);
}

.icon-btn.warn {
  background: rgba(255, 77, 79, .18);
}

.icon-btn.warn:hover {
  background: rgba(255, 77, 79, .28);
}

/* Selector idioma */
.segmented {
  display: inline-flex;
  background: rgba(255, 255, 255, .14);
  border-radius: 10px;
  padding: 2px;
}

.seg-btn {
  border: none;
  cursor: pointer;
  color: #fff;
  background: transparent;
  padding: 6px 10px;
  border-radius: 8px;
  font-size: 13px;
  line-height: 1;
  transition: background .15s;
}

.seg-btn.active {
  background: rgba(255, 255, 255, .28);
}

.seg-btn:hover {
  background: rgba(255, 255, 255, .22);
}

/* Mensajes */
.chatbot-messages {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 14px;
  background: #f5f7fb;
}

.message {
  display: flex;
  margin: 8px 0;
}

.message.bot {
  justify-content: flex-start;
}

.message.bot .bubble.buttons {
  margin-bottom: 16px;
}

.message.user {
  justify-content: flex-end;
}

.bubble {
  max-width: 85%;
  display: inline-flex;
  flex-direction: column;
}

.bubble-text {
  padding: 10px 14px;
  border-radius: 14px;
  font-size: 14px;
  line-height: 1.45;
  box-shadow: 0 2px 4px rgba(0, 0, 0, .08);
  white-space: pre-wrap;
  word-wrap: break-word;
  word-break: break-word;
}

.bubble.user .bubble-text {
  background: #3B81F6;
  color: #fff;
  border-bottom-right-radius: 4px;
}

.bubble.bot .bubble-text {
  background: #fff;
  color: #333;
  border-bottom-left-radius: 4px;
}

/* Botones dentro de mensajes */
.buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
  margin-bottom: 12px;
  /* 👈 separa los botones del siguiente mensaje */
}

.buttons button {
  background: #eef2ff;
  border: 1px solid #d7defc;
  padding: 6px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.2s;
}

.buttons button:hover {
  background: #d7defc;
}

/* Escribiendo */
.typing {
  background: #fff;
  color: #666;
  border-radius: 14px;
  padding: 10px 12px;
  gap: 6px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, .06);
}

.dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #c4c9d6;
  display: inline-block;
  animation: blink 1s infinite ease-in-out;
}

.dot:nth-child(2) {
  animation-delay: .15s;
}

.dot:nth-child(3) {
  animation-delay: .30s;
}

@keyframes blink {
  0%, 80%, 100% { opacity: .2; transform: translateY(0); }
  40% { opacity: 1; transform: translateY(-2px); }
}

/* Adjuntos */
.attachments {
  border-top: 1px solid #e6e8ef;
  background: #fff;
  padding: 8px 10px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #eef2ff;
  color: #314169;
  border: 1px solid #d7defc;
  padding: 5px 8px;
  border-radius: 999px;
  max-width: 100%;
}

.chip img {
  width: 22px;
  height: 22px;
  border-radius: 4px;
  object-fit: cover;
}

.chip .name {
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chip .x {
  border: none;
  background: transparent;
  font-size: 16px;
  cursor: pointer;
  color: #314169;
}

/* Input */
.chatbot-input {
  flex: 0 0 60px;
  display: flex;
  align-items: center;
  gap: 10px;
  border-top: 1px solid #e6e8ef;
  background: #fff;
  padding: 10px;
}

.chatbot-input input {
  flex: 1;
  height: 40px;
  border: 1px solid #d9dce6;
  border-radius: 12px;
  padding: 0 12px;
  outline: none;
  font-size: 14px;
  background: #fff;
}

.attach-btn {
  height: 40px;
  padding: 0 12px;
  border: 1px solid #d9dce6;
  border-radius: 12px;
  background: #f8f9ff;
  cursor: pointer;
}

.send-btn {
  height: 40px;
  padding: 0 18px;
  border: none;
  cursor: pointer;
  border-radius: 12px;
  font-size: 16px;
  color: #fff;
  background: #3B81F6;
}

.send-btn:disabled,
.chatbot-input input:disabled,
.attach-btn:disabled {
  opacity: .6;
  cursor: not-allowed;
}

/* Móvil */
@media (max-width: 600px) {
  .chatbot-window {
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100vh;
    border-radius: 0;
  }

  .chatbot-window.maximized {
    left: 0;
    transform: none;
    width: 100%;
    height: 100vh;
    top: 0;
    border-radius: 0;
  }
}
</style>
